import { useState } from "react";

import {
  Button,
  ButtonGroup,
  Colors,
  Intent,
  Menu,
  MenuDivider,
  MenuItem,
  Portal,
  Position,
} from "@blueprintjs/core";
import { useMediaQuery } from "react-responsive";
import xss from "xss";

import { Popover } from "@/components/helpers/ui/blueprint-overrides/Popover";
import { RichContentRenderer } from "@/components/helpers/ui/RichContentRenderer";
import { Box, Flex } from "@/components/layout/flexbox";
import { isXsQuery } from "@/components/layout/Media";
import { CircleIconButton } from "@/components/pieces/form/CircleIconButton";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { Overlay } from "@/components/pieces/overlay/Overlay";
import { Avatar } from "@/components/pieces/users/Avatar";
import { CTA_COLOR } from "@/css/constants";
import { Note as NoteType } from "@/types/users";
import { formatDate } from "@/utils/datetime";

interface MobileMenuContextProps {
  isAuthor: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onCancel: () => void;
}

function MobileMenuContext({
  onEdit,
  onDelete,
  onCancel,
  isAuthor,
}: MobileMenuContextProps) {
  return (
    <>
      <Portal>
        <Overlay
          top="auto"
          visible={true}
          onClose={onCancel}
          style={{
            width: "100%",
            height: "100%",
            zIndex: 500,
          }}
        >
          <Box
            opacity={0.8}
            bg={Colors.DARK_GRAY2}
            width={1}
            height="100%"
            style={{
              top: 0,
              left: 0,
              zIndex: 1100,
              position: "absolute",
            }}
          />
          <Flex
            width={1}
            height="100%"
            flexDirection="column"
            flexWrap="nowrap"
          >
            <Flex
              flexDirection="column"
              mt="auto"
              p={1}
              style={{ zIndex: 1500 }}
            >
              <ButtonGroup vertical={true}>
                <Button
                  text="Note Options"
                  style={{
                    height: "40px",
                    fontSize: "13px",
                    lineHeight: "18px",
                    fontWeight: 600,
                    background: Colors.LIGHT_GRAY1,
                  }}
                />

                {isAuthor ? (
                  <>
                    <Button
                      style={{
                        height: "60px",
                        color: CTA_COLOR,
                        fontSize: "20px",
                        lineHeight: "25px",
                        background: Colors.LIGHT_GRAY1,
                      }}
                      onClick={onEdit}
                      text="Edit"
                    />
                    <Button
                      style={{
                        height: "60px",
                        color: CTA_COLOR,
                        fontSize: "20px",
                        lineHeight: "25px",
                        background: Colors.LIGHT_GRAY1,
                      }}
                      onClick={onDelete}
                      text="Delete"
                    />
                  </>
                ) : (
                  <Button
                    text="No Options"
                    style={{
                      height: "120px",
                      color: CTA_COLOR,
                      fontSize: "20px",
                      lineHeight: "25px",
                      background: Colors.LIGHT_GRAY1,
                    }}
                  />
                )}
              </ButtonGroup>
              <Button
                text="Cancel"
                style={{
                  fontWeight: 600,
                  marginTop: "8px",
                  height: "60px",
                  background: Colors.LIGHT_GRAY5,
                  color: CTA_COLOR,
                  fontSize: "20px",
                  lineHeight: "25px",
                }}
              />
            </Flex>
          </Flex>
        </Overlay>
      </Portal>
    </>
  );
}

interface DesktopMenuContextProps {
  isAuthor: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function DesktopMenuContext({
  isAuthor,
  onEdit,
  onDelete,
}: DesktopMenuContextProps) {
  return (
    <>
      <Box mx={1} flexBasis="26px" flexShrink="0">
        {isAuthor ? (
          <Popover
            position={Position.BOTTOM}
            canEscapeKeyClose={true}
            content={
              <Menu>
                <MenuItem text="Edit" icon="edit" onClick={onEdit} />
                <MenuDivider />
                <MenuItem
                  text="Delete"
                  icon="trash"
                  intent={Intent.DANGER}
                  onClick={onDelete}
                />
              </Menu>
            }
          >
            <CircleIconButton
              icon="more"
              iconSize={16}
              size={36}
              onClick={() => null}
            />
          </Popover>
        ) : null}
      </Box>
    </>
  );
}

const isEdited = ({
  createdAt,
  updatedAt,
}: {
  createdAt: string;
  updatedAt: string;
}) => {
  if (!createdAt || !updatedAt) return false;
  const createdAtDate = new Date(createdAt);
  const updatedAtDate = new Date(updatedAt);

  return (
    createdAtDate.getSeconds() !== updatedAtDate.getSeconds() ||
    createdAtDate.getMinutes() !== updatedAtDate.getMinutes() ||
    createdAtDate.getHours() !== updatedAtDate.getHours() ||
    createdAtDate.getDay() !== updatedAtDate.getDay() ||
    createdAtDate.getMonth() !== updatedAtDate.getMonth() ||
    createdAtDate.getFullYear() !== updatedAtDate.getFullYear()
  );
};

interface NoteViewProps {
  note: NoteType;
  isAuthor: boolean;
  showAuthor: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

export function NoteView({
  note,
  isAuthor,
  showAuthor,
  onEdit,
  onDelete,
}: NoteViewProps) {
  const [mobileContext, setMobileContext] = useState(false);
  const isXs = useMediaQuery(isXsQuery);
  const toggleMobileContext = () => {
    setMobileContext((x) => !x);
  };
  const { content, author, updatedAt } = note;
  return (
    <Clickable onClick={isXs ? toggleMobileContext : undefined}>
      <Box
        className="note-view"
        mt="12px"
        width={1}
        style={{
          borderRadius: "10px",
          border: `1px solid ${Colors.LIGHT_GRAY3}`,
          padding: isXs ? "14px" : "16px",
        }}
      >
        <Flex>
          <Box flexGrow="1">
            <RichContentRenderer content={xss(content || "")} />
            <Flex
              mt={12}
              alignItems={["flex-start", "center"]}
              fontSize="14px"
              flexDirection={["column", "row"]}
            >
              {showAuthor ? (
                <Flex mr={2} alignItems="center">
                  <Avatar size={24} user={author} />
                  <Box ml={1}>{author.name}</Box>
                </Flex>
              ) : null}
              {formatDate(updatedAt, "MM/dd/yy h:mm a")}
              {isEdited(note) ? <Box color={Colors.GRAY3}>Edited</Box> : null}
            </Flex>
          </Box>
          {isXs ? null : (
            <DesktopMenuContext
              onEdit={onEdit}
              onDelete={onDelete}
              isAuthor={isAuthor}
            />
          )}
        </Flex>
        {isXs && mobileContext ? (
          <MobileMenuContext
            isAuthor={isAuthor}
            onEdit={onEdit}
            onDelete={onDelete}
            onCancel={toggleMobileContext}
          />
        ) : null}
      </Box>
    </Clickable>
  );
}
